import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, TrendingUp, Users } from "lucide-react";

interface WelcomeScreenProps {
  onGetStarted: () => void;
  onSignIn: () => void;
}

export const WelcomeScreen = ({ onGetStarted, onSignIn }: WelcomeScreenProps) => {
  const { t } = useTranslation();
  const benefits = [
    {
      icon: Camera,
      title: t("onboarding.welcome.trackActivities", { defaultValue: "Track Activities" }),
      description: t("onboarding.welcome.trackActivitiesDesc", { defaultValue: "Capture farm work with photos and voice notes" }),
      highlight: t("onboarding.welcome.earnPoints", { defaultValue: "Earn Points" })
    },
    {
      icon: TrendingUp,
      title: t("onboarding.welcome.buildCredit", { defaultValue: "Build Credit" }),
      description: t("onboarding.welcome.buildCreditDesc", { defaultValue: "Convert your daily efforts into credit score" }),
      highlight: t("onboarding.welcome.accessLoans", { defaultValue: "Access Loans" })
    },
    {
      icon: Users,
      title: t("onboarding.welcome.learnNetwork", { defaultValue: "Learn from Network" }),
      description: t("onboarding.welcome.learnNetworkDesc", { defaultValue: "Connect with farmers and share knowledge" }),
      highlight: t("onboarding.welcome.growBetter", { defaultValue: "Grow Better" })
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-12 pb-8">
        <div className="w-48 h-48 bg-primary/20 rounded-full flex items-center justify-center mb-6 animate-fade-in">
          <div className="w-36 h-36 bg-primary/40 rounded-full flex items-center justify-center">
            <Camera className="w-16 h-16 text-primary" />
          </div>
        </div>
        
        <h1 className="text-4xl font-bold text-center mb-3 text-foreground animate-fade-in">
          {t("onboarding.welcome.brand", { defaultValue: "AGXL CBS" })}
        </h1>
        
        <p className="text-xl text-center text-muted-foreground mb-12 animate-fade-in">
          {t("onboarding.welcome.tagline", { defaultValue: "Grow Your Farm, Build Your Credit" })}
        </p>

        {/* Benefits Cards */}
        <div className="w-full max-w-md space-y-4 mb-8">
          {benefits.map((benefit, index) => (
            <Card 
              key={index}
              className="p-4 hover-scale animate-fade-in bg-card/50 backdrop-blur-sm border-border/50"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1">
                    {benefit.title} → <span className="text-primary">{benefit.highlight}</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {benefit.description}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 pb-8 space-y-3">
        <Button 
          onClick={onGetStarted}
          size="lg"
          className="w-full"
          variant="primary"
        >
          {t("onboarding.welcome.getStarted", { defaultValue: "Get Started" })}
        </Button>

        <Button
          onClick={onSignIn}
          variant="ghost"
          size="lg"
          className="w-full"
        >
          {t("onboarding.welcome.alreadyHaveAccount", { defaultValue: "I Already Have an Account" })}
        </Button>
      </div>
    </div>
  );
};
